import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Backdrop, CircularProgress } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { TextField } from "formik-material-ui";
import DatePickerField from "../../customFiles/DatePicker";
import billDetailsFormConstants from "../../utils/constants/billDetailsFormConstants.json";
import SearchDropdown from "../../customFiles/searchDropDown.js";
import axios from "axios";
import { dateConverter, TIMEZONES } from "../../../utils/dateUtils.js";
import {
  WIPRO_ENTITY_ID_LIST,
  PRESCRIPTION_QUESTION_ENTITY_ID_LIST,
  NESTLE_ENTITY_ID_LIST,
} from "../../../config/constants.js";
import {
  isFieldRequired as isFieldRequiredUtil,
  EMERGENCY_PATHOLOGY_CONTRACT,
  PRESCRIBED_PHARMACY_CONTRACT,
  PHARMACY_CONTRACT,
  LocalStorageKeys,
  HEALTH_CHECK,
  DEPENDENTS_PREVENTIVE_HEALTH_CHECK_UP,
  ANNUAL_HEALTH_CHECK_UP,
  VACCINATION,
  limitToTwoDecimals,
  isWholeNumberAmountEntity,
} from "../../utils/constants/constants.js";
import axiosInstance from "../../../utils/axiosInterceptor.js";
import { getMbServerToken } from "../../../utils/cookieUtils";

export default function BillDetails(props) {
  // New props for edit mode and initial values.
  const {
    editMode = false,
    initialValues = {},
    fileInputRefs,
    billDates,
  } = props;
  const apiUrl = process.env.REACT_APP_REIMBURSEMENT_URL;
  const { values: formikValues, setFieldValue } = useFormikContext();
  const contract = props.billType;
  const dynamicContracts = [
    "Prescribed_Diagnostics(Lab_Test)",
    "Inclinic_Doctor_Consultation",
  ];
  const contractBasedFields = billDetailsFormConstants.contractBasedFields;
  const contractWiseFieldLabels =
    billDetailsFormConstants.contractWiseFieldLabels;
  const doctorSpeciality = billDetailsFormConstants.dropDown.doctorSpeciality;
  const illnessType = billDetailsFormConstants.dropDown.illnessType;
  const treatmentType = billDetailsFormConstants.dropDown.treatmentType;

  const selectedPolicy = JSON.parse(localStorage.getItem("selectedPolicy"));

  const [loading, setLoading] = useState(false);
  const [apiFailureMessage, setApiFailureMessage] = useState(null);
  const [apiCall, setApiCall] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [areaPincode, setAreaPincode] = useState();
  const [doctorSpecialisation, setDoctorSpecialisation] = useState();
  const [hospitalList, setHospitalList] = useState({});
  const [doctorList, setDoctorList] = useState({});
  const [selectedClinicName, setCenterName] = useState();
  const [doctor, setDoctorName] = useState();
  const [illness, setIllness] = useState();
  const [treatment, setTreatment] = useState();
  const hasInitialized = useRef(false);

  // Detect if current entity requires the prescription availability question
  const currentEntityId = Number(
    localStorage.getItem(LocalStorageKeys.CURRENT_PM_ENTITY_ID),
  );
  const isPrescriptionQuestionEntity =
    (contract === PRESCRIBED_PHARMACY_CONTRACT ||
      contract === PHARMACY_CONTRACT) &&
    PRESCRIPTION_QUESTION_ENTITY_ID_LIST.includes(currentEntityId);

  const isNestleEntity = NESTLE_ENTITY_ID_LIST.includes(currentEntityId);

  const PRESCRIPTION_REASON_OPTIONS =
    billDetailsFormConstants.dropDown.prescriptionReason || [];

  const getFieldLabel = (fieldName) => {
    const entityKey = NESTLE_ENTITY_ID_LIST.includes(currentEntityId)
      ? "nestle"
      : currentEntityId;
    const entitySpecificLabels =
      billDetailsFormConstants.entitySpecificLabels?.[entityKey];

    if (entitySpecificLabels && entitySpecificLabels[fieldName]?.[contract]) {
      return entitySpecificLabels[fieldName][contract];
    }

    return contractWiseFieldLabels[fieldName]?.[contract] || "";
  };

  const fieldRefs = {
    treatmentType: useRef(),
    otherTreatmentType: useRef(),
    doctorSpeciality: useRef(),
    pincode: useRef(),
    centerName: useRef(),
    otherCenterName: useRef(),
    centerAddress: useRef(),
    dynamicDoctorName: useRef(),
    otherDoctorName: useRef(),
    doctorName: useRef(),
    doctorRegisteredNumber: useRef(),
    illnessType: useRef(),
    otherIllnessType: useRef(),
    billNumber: useRef(),
    billDate: useRef(),
    billAmount: useRef(),
    hasPrescription: useRef(),
    prescriptionReason: useRef(),
  };

  // In BillDetails or FormikForm, wherever your focus logic is:
  props.setFocusOnError((errors) => {
    const errorField = Object.keys(errors).find(
      (key) =>
        (fieldRefs[key] && fieldRefs[key].current) ||
        fileInputRefs.current[key],
    );
    if (errorField) {
      if (fieldRefs[errorField] && fieldRefs[errorField].current) {
        const field = fieldRefs[errorField].current;
        field.focus?.();
        const container = document.querySelector(".formBorder");
        if (container && container.contains(field)) {
          field.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      } else if (fileInputRefs.current[errorField]) {
        const fileInput = fileInputRefs.current[errorField];
        fileInput.focus?.();
        const container =
          document.querySelector(".formBorder") ||
          document.querySelector(".box");
        if (container && container.contains(fileInput)) {
          fileInput.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }
    }
  });

  const resetStateFunctions = useCallback(() => {
    setIllness();
    setCenterName();
    setDoctorName();
    setAreaPincode();
    setDoctorList({});
    setLoading(false);
    setHospitalList({});
    setIsDisabled(true);
    setDoctorSpecialisation();
    setApiFailureMessage(null);
  }, []);

  /**
   * NOT USED CURRENTLY!
   * Fetches clinic and doctor details based on pincode and speciality.
   */
  const fetchClinicAndDoctorDetails = async (pincode, speciality) => {
    if (!validationCheckForSearchApi) return false;

    const authToken = getMbServerToken();

    const authHeader = {
      headers: {
        authtoken: authToken,
      },
    };
    const searchApiPayload = {
      contractType:
        contract === "Inclinic_Doctor_Consultation" ? "consults" : "labs",
      pincode: parseInt(pincode),
    };
    if (searchApiPayload.contractType === "consults")
      searchApiPayload["speciality"] = speciality;
    try {
      setLoading(true);
      const searchApiResponse = await axiosInstance.post(
        `${apiUrl}/search/suggestion`,
        searchApiPayload,
        authHeader,
      );
      if (searchApiResponse.data.isSuccess) {
        const doctorDetails = partitionResponseByLocation(
          searchApiResponse.data.result,
          "dcName",
        );
        const clinicDetails = fetchClinicDetails(searchApiResponse.data.result);
        const doctorDetailsClinicWise =
          fetchDoctorDetailsClinicWise(doctorDetails);

        setCenterName();
        setLoading(false);
        setApiCall(false);
        setIsDisabled(false);
        setApiFailureMessage(null);
        setHospitalList(clinicDetails);
        setDoctorList(doctorDetailsClinicWise);
      } else {
        setAreaPincode();
        setApiFailureMessage(
          "Failed to fetch " +
            contractWiseFieldLabels.centerName[contract] +
            " details please enter pincode again",
        );
        setLoading(false);
      }
    } catch (error) {
      setApiFailureMessage(
        "Failed to fetch " +
          contractWiseFieldLabels.centerName[contract] +
          " details please enter pincode again",
      );
      setLoading(false);
    }
  };

  const validationCheckForSearchApi = (pincode, speciality) => {
    if (!/[1-9]{1}[0-9]{5}|[1-9]{1}[0-9]{3}\s[0-9]{3}/.test(pincode)) {
      return false;
    } else if (contract === "Inclinic_Doctor_Consultation" && !speciality) {
      return false;
    }
    return true;
  };

  const validatePin = async (pincode, specialisation, isPincodeChanged) => {
    let error;
    if (!/[1-9]{1}[0-9]{5}|[1-9]{1}[0-9]{3}\s[0-9]{3}/.test(pincode)) {
      error = "Please enter a valid pincode";
    } else {
      setAreaPincode(pincode);
      // API call logic commented out for now.
    }
    return error;
  };

  const fetchDoctorDetailsClinicWise = (doctorDetails) => {
    let doctorDetailsArr = {};
    doctorDetailsArr["others"] = [];
    for (let key in doctorDetails) {
      let clinicName = key.replace(/ /g, "_");
      doctorDetailsArr[clinicName] = [];
      for (let i = 0; i < doctorDetails[key].length; i++) {
        const doctorName = "Dr. " + doctorDetails[key][i].doctorName;
        doctorDetailsArr[clinicName].push({
          label: doctorName,
          value: doctorName,
        });
      }
      doctorDetailsArr[clinicName].push({ label: "others", value: "others" });
    }
    doctorDetailsArr["others"].push({ label: "others", value: "others" });
    return doctorDetailsArr;
  };

  const fetchClinicDetails = (clinicAndDoctorDetails) => {
    let clinicDetailsArr = [];
    for (let i = 0; i < clinicAndDoctorDetails.length; i++) {
      clinicDetailsArr.push({
        label: clinicAndDoctorDetails[i].dcName,
        value: clinicAndDoctorDetails[i].dcName,
      });
    }
    clinicDetailsArr.push({
      label: "My clinic/hopsital is not in the list",
      value: "others",
    });
    return clinicDetailsArr;
  };

  const partitionResponseByLocation = (data, key) => {
    return data.reduce(function (row, val) {
      (row[val[key]] = row[val[key]] || []).push(val);
      return row;
    }, {});
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      resetStateFunctions();
      hasInitialized.current = true;
    }

    // Initialize state variables from initialValues when in edit mode
    if (editMode && initialValues && Object.keys(initialValues).length > 0) {
      if (initialValues.treatmentType === "others") {
        setTreatment("others");
      }
      if (initialValues.illnessType === "others") {
        setIllness("others");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, resetStateFunctions]);

  const isFieldRequired = (fieldName) => {
    // Special handling for doctorName in Nestle entity - conditional based on hasPrescription
    if (
      fieldName === "doctorName" &&
      isNestleEntity &&
      (contract === PRESCRIBED_PHARMACY_CONTRACT ||
        contract === PHARMACY_CONTRACT)
    ) {
      return formikValues.hasPrescription === "yes";
    }
    return isFieldRequiredUtil(fieldName, contract, billDetailsFormConstants);
  };

  /**
   * 19/06/2025
   * Now we receive `minBillDate` in the response of the bill category rules object in the `getRules` API.
   * Logic to compute dates is as follows now -
   * For Min Bill Date -
   * 1. If `minBillDate` is present, use it.
   * 2. If `minBillDate` is not present, use the policy start date.
   *
   * For Max Bill Date -
   * 1. If the entity id is Wipro, the maxBillDate will be a quarterEndDate.
   * 2. If the entity is Wipro, and quarterEndDate is not present, use policy end date.
   * 3. If the entity is not Wipro, the maxBillDate will be the policy end date.
   *
   * 15/09/2025
   * Updated logic to fetch min and max bill dates from local storage categoriesV2 object.
   * If not present, fallback to policy start and end dates.
   */
  const computedDatesForDatePicker = useMemo(() => {
    if (billDates) return billDates;
    let internalBillDates = { minDate: null, maxDate: null };
    try {
      const categoriesData =
        JSON.parse(localStorage.getItem("categoriesV2")) || [];
      if (Array.isArray(categoriesData)) {
        const contractData = categoriesData.find(
          (cat) => cat.category === contract,
        );
        if (!contractData || !contractData.minBillDate) {
          internalBillDates.minDate = selectedPolicy?.subscriptionStartDate;
          internalBillDates.maxDate = selectedPolicy?.subscriptionEndDate;
        } else {
          internalBillDates.minDate = contractData.minBillDate;
          if (WIPRO_ENTITY_ID_LIST.includes(selectedPolicy?.entityId)) {
            internalBillDates.maxDate = contractData.maxBillDate;
          } else {
            internalBillDates.maxDate = selectedPolicy?.subscriptionEndDate;
          }
        }
      }
    } catch (error) {
      console.error(error);
      internalBillDates.minDate = selectedPolicy?.subscriptionStartDate;
      internalBillDates.maxDate = selectedPolicy?.subscriptionEndDate;
    }
    return internalBillDates;
  }, [billDates, selectedPolicy]);

  return (
    <>
      {!loading ? (
        <Grid container spacing={2}>
          {/* Prescription Question - Show first for Prescribed_Pharmacy entities */}
          {isPrescriptionQuestionEntity && (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                id="hasPrescription"
                name="hasPrescription"
                required
              >
                {({ field }) => (
                  <SearchDropdown
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    label="Do you have a prescription?"
                    name={field.name}
                    required
                    initialValue={
                      formikValues.hasPrescription
                        ? {
                            label:
                              formikValues.hasPrescription === "yes"
                                ? "Yes"
                                : "No",
                            value: formikValues.hasPrescription,
                          }
                        : null
                    }
                    ref={fieldRefs.hasPrescription}
                  />
                )}
              </Field>
            </Grid>
          )}

          {/* Reason field — shown only when hasPrescription === "no" */}
          {isPrescriptionQuestionEntity &&
            formikValues.hasPrescription === "no" && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  id="prescriptionReason"
                  name="prescriptionReason"
                  required
                >
                  {({ field }) => (
                    <SearchDropdown
                      options={PRESCRIPTION_REASON_OPTIONS}
                      label="Reason for non availability of Prescription"
                      name={field.name}
                      required
                      initialValue={
                        formikValues.prescriptionReason
                          ? {
                              label: formikValues.prescriptionReason,
                              value: formikValues.prescriptionReason,
                            }
                          : null
                      }
                      ref={fieldRefs.prescriptionReason}
                    />
                  )}
                </Field>
              </Grid>
            )}

          {/* Moved illnessType to top for Emergency Pathology */}
          {contract === EMERGENCY_PATHOLOGY_CONTRACT &&
            contractBasedFields.illnessType.includes(contract) && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  id="illnessType"
                  name="illnessType"
                  required={isFieldRequired("illnessType")}
                >
                  {({ field }) => (
                    <SearchDropdown
                      onSelect={(event) =>
                        event?.value
                          ? setIllness(event.value)
                          : setIllness(null)
                      }
                      options={
                        WIPRO_ENTITY_ID_LIST.includes(
                          Number(selectedPolicy?.entityId),
                        )
                          ? [
                              {
                                label: "Emergency Pathology",
                                value: "Emergency Pathology",
                              },
                              {
                                label: "Non serviceable Pathology",
                                value: "Non serviceable Pathology",
                              },
                            ]
                          : illnessType[contract]
                            ? illnessType[contract]
                            : illnessType["default"]
                      }
                      label={contractWiseFieldLabels.illnessType[contract]}
                      name={field.name}
                      required={isFieldRequired("illnessType")}
                      ref={fieldRefs.illnessType}
                    />
                  )}
                </Field>
              </Grid>
            )}
          {contractBasedFields.treatmentType.includes(contract) &&
            (contract === HEALTH_CHECK ||
              contract === DEPENDENTS_PREVENTIVE_HEALTH_CHECK_UP ||
              contract === ANNUAL_HEALTH_CHECK_UP ||
              contract === VACCINATION) && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="treatmentType"
                  name="treatmentType"
                  label={contractWiseFieldLabels.treatmentType[contract]}
                  required={isFieldRequired("treatmentType")}
                  inputRef={fieldRefs.treatmentType}
                />
              </Grid>
            )}

          {contractBasedFields.treatmentType.includes(contract) &&
            contract !== HEALTH_CHECK &&
            contract !== DEPENDENTS_PREVENTIVE_HEALTH_CHECK_UP &&
            contract !== ANNUAL_HEALTH_CHECK_UP &&
            contract !== VACCINATION && (
              <Grid item xs={12} md={12}>
                {treatmentType[contract] ? (
                  <Field
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                    id="treatmentType"
                    name="treatmentType"
                    required={isFieldRequired("treatmentType")}
                  >
                    {({ field }) => (
                      <SearchDropdown
                        onSelect={(event) =>
                          event?.value
                            ? setTreatment(event.value)
                            : setTreatment(null)
                        }
                        options={treatmentType[contract]}
                        label={contractWiseFieldLabels.treatmentType[contract]}
                        name={field.name}
                        required={isFieldRequired("treatmentType")}
                        ref={fieldRefs.treatmentType}
                      />
                    )}
                  </Field>
                ) : (
                  <Field
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                    component={TextField}
                    id="treatmentType"
                    name="treatmentType"
                    label={contractWiseFieldLabels.treatmentType[contract]}
                    required={isFieldRequired("treatmentType")}
                    inputRef={fieldRefs.treatmentType}
                  />
                )}
              </Grid>
            )}

          {contractBasedFields.treatmentType.includes(contract) &&
            contract !== HEALTH_CHECK &&
            contract !== DEPENDENTS_PREVENTIVE_HEALTH_CHECK_UP &&
            contract !== ANNUAL_HEALTH_CHECK_UP &&
            contract !== VACCINATION &&
            (treatment === "others" ||
              initialValues.treatmentType === "others" ||
              formikValues.treatmentType === "others") && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="otherTreatmentType"
                  name="otherTreatmentType"
                  label={contractWiseFieldLabels.treatmentType[contract]}
                  required={isFieldRequired("treatmentType")}
                  inputRef={fieldRefs.otherTreatmentType}
                />
              </Grid>
            )}

          {contractBasedFields.doctorSpeciality.includes(contract) && (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                id="doctorSpeciality"
                name="doctorSpeciality"
                inputProps={{
                  style: { borderRadius: 80 },
                }}
                variant="filled"
                required={isFieldRequired("doctorSpeciality")}
              >
                {({ field }) => (
                  <SearchDropdown
                    onSelect={(event) =>
                      event?.value
                        ? (setDoctorSpecialisation(event.value),
                          setApiCall(true))
                        : (setDoctorSpecialisation(null), setApiCall(false))
                    }
                    options={
                      doctorSpeciality[contract]
                        ? doctorSpeciality[contract]
                        : doctorSpeciality["default"]
                    }
                    label={contractWiseFieldLabels.doctorSpeciality[contract]}
                    name={field.name}
                    isDisabled={false}
                    required={isFieldRequired("doctorSpeciality")}
                    ref={fieldRefs.doctorSpeciality}
                  />
                )}
              </Field>
            </Grid>
          )}

          {contractBasedFields.pincode.includes(contract) &&
            !isNestleEntity && (
              <Grid item xs={12} md={12}>
                <Field
                  type="text"
                  inputProps={{
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                    maxLength: 6,
                    "aria-label": "Pincode. Enter each digit one by one.",
                    role: "textbox",
                  }}
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="pincode"
                  name="pincode"
                  required={isFieldRequired("pincode")}
                  inputRef={fieldRefs.pincode}
                  label={contractWiseFieldLabels.pincode[contract]}
                />
              </Grid>
            )}

          {apiFailureMessage && (
            <Grid item xs={12} md={12}>
              <p style={{ color: "#FC1721" }}>{apiFailureMessage}</p>
            </Grid>
          )}

          {Object.values(hospitalList).length > 1 &&
            contractBasedFields.centerName.includes(contract) && (
              <Grid item xs={12} md={12}>
                <div className="favCheck">
                  <Field
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                    id="centerName"
                    name="centerName"
                    required={isFieldRequired("centerName")}
                  >
                    {({ field }) => (
                      <SearchDropdown
                        onSelect={(event) =>
                          event?.value
                            ? setCenterName(event.value.replace(/ /g, "_"))
                            : setCenterName(null)
                        }
                        options={hospitalList}
                        label={getFieldLabel("centerName")}
                        name={field.name}
                        isDisabled={isDisabled}
                        ref={fieldRefs.centerName}
                      />
                    )}
                  </Field>
                </div>
              </Grid>
            )}

          {Object.values(hospitalList).length < 1 &&
            contractBasedFields.centerName.includes(contract) && (
              <Grid item xs={12} md={12}>
                <div className="favCheck">
                  <Field
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                    component={TextField}
                    id="centerName"
                    name="centerName"
                    label={getFieldLabel("centerName")}
                    isDisabled={isDisabled}
                    required={isFieldRequired("centerName")}
                    inputRef={fieldRefs.centerName}
                  />
                </div>
              </Grid>
            )}

          {dynamicContracts.includes(contract) &&
            selectedClinicName === "others" && (
              <Grid item xs={12} md={12}>
                <div className="favCheck">
                  <Field
                    sx={{ width: { xs: "100%", sm: "100%" } }}
                    component={TextField}
                    id="otherCenterName"
                    name="otherCenterName"
                    required={isFieldRequired("centerName")}
                    label={getFieldLabel("centerName")}
                    inputRef={fieldRefs.otherCenterName}
                  />
                </div>
              </Grid>
            )}

          {contractBasedFields.centerAddress.includes(contract) && (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="centerAddress"
                name="centerAddress"
                required={isFieldRequired("centerAddress")}
                label={getFieldLabel("centerAddress")}
                inputRef={fieldRefs.centerAddress}
              />
            </Grid>
          )}

          {contractBasedFields.pincode.includes(contract) && isNestleEntity && (
            <Grid item xs={12} md={12}>
              <Field
                inputProps={{
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  maxLength: 6,
                }}
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="pincode"
                name="pincode"
                required={isFieldRequired("pincode")}
                inputRef={fieldRefs.pincode}
                label={getFieldLabel("pincode")}
              />
            </Grid>
          )}

          {contract === "Inclinic_Doctor_Consultation" && selectedClinicName ? (
            contractBasedFields.doctorName.includes(contract) &&
            selectedClinicName !== "others" ? (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  id="dynamicDoctorName"
                  name="dynamicDoctorName"
                  required={isFieldRequired("doctorName")}
                >
                  {({ field }) => (
                    <SearchDropdown
                      onSelect={(event) =>
                        event?.value
                          ? setDoctorName(event.value)
                          : setDoctorName(null)
                      }
                      options={doctorList[selectedClinicName]}
                      label={contractWiseFieldLabels.doctorName[contract]}
                      name={field.name}
                      isDisabled={isDisabled || !selectedClinicName}
                      ref={fieldRefs.doctorName}
                    />
                  )}
                </Field>
              </Grid>
            ) : (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="otherDoctorName"
                  name="otherDoctorName"
                  required={isFieldRequired("doctorName")}
                  inputRef={fieldRefs.otherDoctorName}
                  label={contractWiseFieldLabels.doctorName[contract]}
                />
              </Grid>
            )
          ) : contractBasedFields.doctorName.includes(contract) ? (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="doctorName"
                name="doctorName"
                required={isFieldRequired("doctorName")}
                inputRef={fieldRefs.doctorName}
                label={contractWiseFieldLabels.doctorName[contract]}
              />
            </Grid>
          ) : null}

          {contractBasedFields.doctorName.includes(contract) &&
            doctor === "others" && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="otherDoctorName"
                  name="otherDoctorName"
                  required={isFieldRequired("doctorName")}
                  inputRef={fieldRefs.otherDoctorName}
                  label={contractWiseFieldLabels.doctorName[contract]}
                />
              </Grid>
            )}

          {/* {contractBasedFields.doctorRegisteredNumber.includes(contract) && (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="doctorRegisteredNumber"
                name="doctorRegisteredNumber"
                required={isFieldRequired("doctorRegisteredNumber")}
                inputRef={fieldRefs.doctorRegisteredNumber}
                label={contractWiseFieldLabels.doctorRegisteredNumber[contract]}
              />
            </Grid>
          )} */}

          {contract !== EMERGENCY_PATHOLOGY_CONTRACT &&
            contractBasedFields.illnessType.includes(contract) && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  id="illnessType"
                  name="illnessType"
                  required={isFieldRequired("illnessType")}
                >
                  {({ field }) => (
                    <SearchDropdown
                      onSelect={(event) =>
                        event?.value
                          ? setIllness(event.value)
                          : setIllness(null)
                      }
                      options={
                        illnessType[contract]
                          ? illnessType[contract]
                          : illnessType["default"]
                      }
                      label={contractWiseFieldLabels.illnessType[contract]}
                      name={field.name}
                      required={isFieldRequired("illnessType")}
                      ref={fieldRefs.illnessType}
                    />
                  )}
                </Field>
              </Grid>
            )}

          {contractBasedFields.illnessType.includes(contract) &&
            (illness === "others" ||
              initialValues.illnessType === "others" ||
              formikValues.illnessType === "others") && (
              <Grid item xs={12} md={12}>
                <Field
                  sx={{ width: { xs: "100%", sm: "100%" } }}
                  component={TextField}
                  id="otherIllnessType"
                  name="otherIllnessType"
                  required={isFieldRequired("illnessType")}
                  inputRef={fieldRefs.otherIllnessType}
                  label={contractWiseFieldLabels.illnessType[contract]}
                />
              </Grid>
            )}

          {contractBasedFields.billNumber.includes(contract) && (
            <Grid item xs={12} md={12}>
              <Field
                type="text"
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="billNumber"
                name="billNumber"
                inputRef={fieldRefs.billNumber}
                required={isFieldRequired("billNumber")}
                label={contractWiseFieldLabels.billNumber[contract]}
                inputProps={{
                  "aria-label": "Bill Number. Enter each character one by one.",
                  role: "textbox",
                }}
              />
            </Grid>
          )}

          {contractBasedFields.billDate.includes(contract) && (
            <Grid item xs={12} md={12}>
              <DatePickerField
                sx={{ width: { xs: "100%", sm: "100%" } }}
                type="date"
                id="billDate"
                name="billDate"
                required={isFieldRequired("billDate")}
                label={contractWiseFieldLabels.billDate[contract]}
                minDate={dateConverter(
                  computedDatesForDatePicker.minDate,
                  TIMEZONES.INDIA,
                  "convertToIST",
                )}
                maxDate={dateConverter(
                  computedDatesForDatePicker.maxDate,
                  TIMEZONES.INDIA,
                  "default",
                )}
                inputRef={fieldRefs.billDate}
              />
            </Grid>
          )}

          {contractBasedFields.billAmount.includes(contract) && (
            <Grid item xs={12} md={12}>
              <Field
                sx={{ width: { xs: "100%", sm: "100%" } }}
                component={TextField}
                id="billAmount"
                name="billAmount"
                required={isFieldRequired("billAmount")}
                label={contractWiseFieldLabels.billAmount[contract]}
                inputRef={fieldRefs.billAmount}
                inputProps={{ inputMode: "decimal" }}
                {...(!isWholeNumberAmountEntity() && {
                  onChange: (e) =>
                    setFieldValue(
                      "billAmount",
                      limitToTwoDecimals(e.target.value),
                    ),
                })}
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </>
  );
}
